import { useRef, useState } from "react";
import { useDolfinAuth } from "@/components/DolfinProvider";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { FileSpreadsheet, Upload, UploadCloud, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/api-error";

/** One bad cell, as the API reports it under the problem detail's `data.errors`. */
interface RowError {
  row: number;
  column: string;
  code: string;
  message: string;
}

const SAMPLE_CSV = [
  "name,description,unitPrice,currency,type",
  "Consulting day,Senior consultant day rate,750.00,GBP,Service",
  "Widget,Standard widget,49.99,GBP,Product",
].join("\n");

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** All-or-nothing: a rejected file names every bad cell at once, so the sheet is fixed in one pass. */
export function BulkUploadProductsDialog({
  open,
  onOpenChange,
  onUploaded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploaded: (created: number) => void;
}) {
  const { authFetch } = useDolfinAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<RowError[]>([]);

  function reset() {
    setFile(null);
    setError(null);
    setRowErrors([]);
    setDragging(false);
    if (inputRef.current) inputRef.current.value = "";
  }

  /** Picking a new file clears the previous attempt's errors, which no longer describe it. */
  function selectFile(next: File | null) {
    if (!next) return;
    setFile(next);
    setError(null);
    setRowErrors([]);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function downloadSample() {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "products-template.csv";
    // Firefox won't follow a click on a detached anchor, and revoking on the same tick can free the blob too early.
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setError(null);
    setRowErrors([]);

    try {
      const body = new FormData();
      body.append("file", file);

      // authFetch: openapi-fetch doesn't model multipart uploads.
      const response = await authFetch("/v1/products/bulk", { method: "POST", body });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        // apiErrorMessage also unpacks RFC 9110 `errors`, where model-level validation failures put the useful text.
        const message = apiErrorMessage(payload, "Failed to upload products.");
        setError(message);
        setRowErrors(Array.isArray(payload?.data?.errors) ? payload.data.errors : []);
        // The toast surfaces the failure when the row table is scrolled out of view.
        toast.error(message);
        return;
      }

      const created = Number(payload?.created ?? 0);
      toast.success(`Created ${created} ${created === 1 ? "product" : "products"}`);
      onUploaded(created);
      handleOpenChange(false);
    } catch (e) {
      console.error("Failed to upload products:", e);
      const message = apiErrorMessage(e, "An unexpected error occurred while uploading the file.");
      setError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk upload products</DialogTitle>
          <DialogDescription>
            Upload a CSV to create many products at once. Either every row is created or none are, so
            a rejected file can safely be fixed and uploaded again.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="products-csv">CSV file</Label>
            {/* Native input hidden: its button has no icon and shows no filename until the OS dialog closes. */}
            <input
              id="products-csv"
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={(e) => selectFile(e.target.files?.[0] ?? null)}
            />
            {file ? (
              <div className="flex items-center gap-3 rounded-lg border bg-muted/40 p-3">
                <FileSpreadsheet className="size-5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={uploading}
                  onClick={() => inputRef.current?.click()}
                >
                  Replace
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  disabled={uploading}
                  aria-label="Remove file"
                  onClick={reset}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  selectFile(e.dataTransfer.files?.[0] ?? null);
                }}
                className={cn(
                  "flex w-full flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-8 text-center transition-colors",
                  "hover:border-primary/50 hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  dragging && "border-primary bg-accent/50",
                )}
              >
                <UploadCloud className="size-6 text-muted-foreground" />
                <span className="text-sm font-medium">Choose a CSV file or drag it here</span>
                {/* The real caps: the server rejects the whole file above either. */}
                <span className="text-xs text-muted-foreground">Accepts .csv, up to 1000 products and 2 MB</span>
              </button>
            )}
          </div>

          <div className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">Columns</p>
            <p className="mt-1">
              <span className="font-mono">name</span>,{" "}
              <span className="font-mono">unitPrice</span> and{" "}
              <span className="font-mono">currency</span> are required.{" "}
              <span className="font-mono">description</span> and{" "}
              <span className="font-mono">type</span> (Product or Service, defaulting to Product) are
              optional. The first row must be the header row.
            </p>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="mt-1 h-auto p-0 text-xs"
              onClick={downloadSample}
            >
              Download a template
            </Button>
          </div>

          {error && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {rowErrors.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">
                {rowErrors.length} {rowErrors.length === 1 ? "problem" : "problems"} found. No products
                were created.
              </p>
              <div className="max-h-64 overflow-y-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted">
                    <tr className="text-left">
                      <th className="w-16 px-3 py-2 font-medium">Row</th>
                      <th className="w-32 px-3 py-2 font-medium">Column</th>
                      <th className="px-3 py-2 font-medium">Problem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowErrors.map((rowError, i) => (
                      <tr key={`${rowError.row}-${rowError.column}-${i}`} className="border-t">
                        <td className="px-3 py-2 tabular-nums">{rowError.row}</td>
                        <td className="px-3 py-2 font-mono text-xs">{rowError.column}</td>
                        <td className="px-3 py-2">{rowError.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                Row numbers count the data rows below the header, so row 1 is the first product.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!file || uploading}>
            {uploading ? <Spinner className="size-4" /> : <Upload className="size-4" />}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
